import type { AxiosRequestConfig } from "axios";
import { BaseResponse } from "@/api/api";
import { normalizeWsRestApi, WSREST_ALLOWED_APIS } from "@/api/wsrest";
import { USE_WSREST } from "@/config/wsrest";
import { $getWithAuth } from "@/utils/request";
import {
  callWsRest,
  closeWsRest,
  isWsRestGatewayFallback,
  wsCallRespToBaseResponse,
} from "@/utils/wsrestClient";
import { useMainStore } from "@/store";

const WS_BODY_SKIP_KEYS = new Set(["timeRange"]);

function isSerializableWsValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(
      (item) =>
        item === null ||
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean"
    );
  }
  return false;
}

export function sanitizeWsBody(
  params: Record<string, unknown>
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (WS_BODY_SKIP_KEYS.has(key)) {
      continue;
    }
    if (!isSerializableWsValue(value)) {
      continue;
    }
    body[key] = value;
  }
  return body;
}

export async function $getWithAuthOrWsRest(
  api: string,
  params: Record<string, unknown> = {},
  config?: AxiosRequestConfig
): Promise<BaseResponse> {
  const normalizedApi = normalizeWsRestApi(api);

  if (!USE_WSREST || !WSREST_ALLOWED_APIS.has(normalizedApi)) {
    return $getWithAuth(api, params, config);
  }

  const auth = useMainStore().token;
  if (auth == undefined) {
    return { msg: "Permission denied" } as BaseResponse;
  }

  const timeout =
    typeof config?.timeout === "number" ? config.timeout : undefined;
  const body = sanitizeWsBody(params);

  try {
    const resp = await callWsRest(normalizedApi, body, timeout);
    if (isWsRestGatewayFallback(resp)) {
      if (resp.code === 403) {
        console.warn("[wsrest] api not allowed, fallback HTTP:", normalizedApi);
      }
      return $getWithAuth(api, params, config);
    }
    return wsCallRespToBaseResponse(resp);
  } catch {
    return $getWithAuth(api, params, config);
  }
}

export { closeWsRest };
